import React, { useState, useEffect } from 'react'
import i18n from './i18n'
import { Routes, Route, Navigate } from 'react-router-dom'
import { TopToolbarProvider } from './context/TopToolbarContext'
import { useAuth } from './context/AuthContext'
import ProtectedRoute from './ProtectedRoute'
import Layout from './layouts/Layout'
import Home from './pages/Home'
import Staff from './pages/Staff/Staff'
import Order from './pages/Order/Order'
import Schedule from './pages/Schedule/Schedule'
import RightPanelPopup from './pages/Schedule/RightPanelPopup'
import LoginPage from './pages/Login/Login'
import OAuth2Callback from './pages/OAuth2Callback'
import PendingApproval from './pages/PendingApproval'
import Inventory from './pages/Inventory/Inventory'
import SettingPage from './pages/Setting/Setting'
import PrivacyPolicy from './pages/Policy'
import FileHandler from './pages/FileHandler/FileHandler'
import MyProfile from './pages/MyProfile/MyProfile'
import UserManagement from './pages/UserManagement/UserManagement'
import UserApproval from './pages/UserApproval/UserApproval'
import NotImplemented from './components/NotImplemented/NotImplemented'

const IndexRedirect = () => {
  return <Navigate to='/login' replace />
}

const App = () => {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false)
  const toggleSidebar = () => setSidebarCollapsed((prev) => !prev)
  const { role, token } = useAuth()

  // Always start with sidebar collapsed after login (and on reload when token exists)
  useEffect(() => {
    if (token) setSidebarCollapsed(false)
  }, [token])

  // Apply saved UI settings (font family/size/weight and language) from localStorage
  useEffect(() => {
    const applySettings = (s) => {
      try {
        if (!s) return
        if (s.fontFamily)
          document.documentElement.style.setProperty(
            '--app-font-family',
            s.fontFamily
          )
        if (s.fontSize)
          document.documentElement.style.setProperty(
            '--app-font-size',
            `${s.fontSize}px`
          )
        if (s.fontWeight)
          document.documentElement.style.setProperty(
            '--app-font-weight',
            s.fontWeight
          )
        if (s.language) i18n.changeLanguage(s.language)
      } catch (e) {
        // ignore
      }
    }

    try {
      const raw = localStorage.getItem('app_settings')
      if (raw) applySettings(JSON.parse(raw))
    } catch (e) {}

    const handler = (ev) => {
      if (ev && ev.detail) applySettings(ev.detail)
    }
    window.addEventListener('appSettingsUpdated', handler)
    return () => window.removeEventListener('appSettingsUpdated', handler)
  }, [])

  return (
    <TopToolbarProvider>
      <Routes>
        <Route path='/login' element={<LoginPage />} />
        <Route path='/privacy-policy' element={<PrivacyPolicy />} />
        <Route path='/auth/google/callback' element={<OAuth2Callback />} />
        <Route path='/pending-approval' element={<PendingApproval />} />

        <Route path='/' element={<IndexRedirect />} />
        <Route
          path='/workspace-select'
          element={<Navigate to='/login' replace />}
        />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute allowedRoles={['user', 'admin']} />}>
          <Route path='/file-handler' element={<FileHandler />} />
          <Route
            path='/home'
            element={
              <Layout
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            }
          >
            <Route index element={<Home />} />
            <Route
              path='/home/staff'
              element={role === 'user' ? <MyProfile /> : <Staff />}
            />
            <Route
              path='/home/users'
              element={
                role === 'admin' ? (
                  <UserManagement />
                ) : (
                  <Navigate to='/home' replace />
                )
              }
            />
            <Route
              path='/home/user-approvals'
              element={
                role === 'admin' ? (
                  <UserApproval />
                ) : (
                  <Navigate to='/home' replace />
                )
              }
            />
            <Route
              path='/home/order'
              element={
                <NotImplemented
                  message={
                    'ただいま開発中です。近日公開予定ですので、ぜひお楽しみに！'
                  }
                />
              }
            />
            <Route
              path='/home/inventory'
              element={
                <NotImplemented
                  message={
                    'ただいま開発中です。近日公開予定ですので、ぜひお楽しみに！'
                  }
                />
              }
            />
            <Route path='/home/schedule' element={<Schedule />} />
            <Route path='/home/settings' element={<SettingPage />} />
            <Route
              path='/home/analytics'
              element={
                <NotImplemented
                  message={
                    'ただいま開発中です。近日公開予定ですので、ぜひお楽しみに！'
                  }
                />
              }
            />
            <Route
              path='/home/supplier'
              element={
                <NotImplemented
                  message={
                    'ただいま開発中です。近日公開予定ですので、ぜひお楽しみに！'
                  }
                />
              }
            />
            <Route
              path='/home/logout'
              element={
                <div className='p-6 text-lg font-semibold text-red-500'>
                  You have logged out.
                </div>
              }
            />
          </Route>
          <Route
            path='/allocation/schedule/right-panel'
            element={<RightPanelPopup />}
          />
        </Route>

        <Route
          path='*'
          element={
            <NotImplemented
              message={
                'ただいま開発中です。近日公開予定ですので、ぜひお楽しみに！'
              }
            />
          }
        />
      </Routes>
    </TopToolbarProvider>
  )
}

export default App
